// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import Users from '@app/features/user/state/Users';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import styles from '@app/features/voice/soundboard/components/SoundboardPopout.module.css';
import {
	DEFAULT_SOUNDBOARD_SOUNDS,
	type SoundboardSound,
} from '@app/features/voice/soundboard/SoundboardPresets';
import SoundboardStore, {
	getServerSoundLimit,
	MAX_PERSONAL_SOUNDBOARD_SLOTS,
} from '@app/features/voice/soundboard/SoundboardStore';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {readBlobAsDataUrl} from '@app/features/platform/utils/BlobDataUrl';
import {
	MagnifyingGlassIcon,
	PlayIcon,
	PlusIcon,
	ShieldCheckIcon,
	SparkleIcon,
	SpeakerHighIcon,
	SpeakerLowIcon,
	SpeakerSlashIcon,
	TrashIcon,
	UploadSimpleIcon,
	WaveformIcon,
	XIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useRef, useState} from 'react';

interface SoundboardPopoutProps {
	onClose?: () => void;
	isModal?: boolean;
	guildId?: string;
}

type SoundboardTab = 'all' | 'server' | 'personal' | 'default';

export const SoundboardPopout: React.FC<SoundboardPopoutProps> = observer(
	({onClose, isModal = false, guildId}) => {
		const [activeTab, setActiveTab] = useState<SoundboardTab>('all');
		const [searchQuery, setSearchQuery] = useState('');
		const [showAddForm, setShowAddForm] = useState(false);
		const [addFormTarget, setAddFormTarget] = useState<'server' | 'personal'>('server');
		const [soundName, setSoundName] = useState('');
		const [soundEmoji, setSoundEmoji] = useState('🔊');
		const [soundUrl, setSoundUrl] = useState('');
		const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
		const [formError, setFormError] = useState<string | null>(null);
		const fileInputRef = useRef<HTMLInputElement>(null);

		// Resolve current guild and voice connection
		const activeVoiceChannel = MediaEngine.channelId
			? Channels.getChannel(MediaEngine.channelId)
			: null;
		const effectiveGuildId = guildId || activeVoiceChannel?.guildId;
		const guild = effectiveGuildId ? Guilds.getGuild(effectiveGuildId) : undefined;

		// Permissions & Membership checks
		const canManageServerSounds = effectiveGuildId
			? Permission.can(Permissions.MANAGE_GUILD, {guildId: effectiveGuildId}) ||
				Permission.can(Permissions.MANAGE_EXPRESSIONS, {guildId: effectiveGuildId})
			: false;

		const currentUserId = Authentication.currentUserId;
		const currentUser = currentUserId ? Users.getUser(currentUserId) : null;
		const isNDPlus = currentUser?.isPremium() ?? false;

		// Slot limits
		const boostTier = guild?.premiumTier ?? 0;
		const maxServerSlots = getServerSoundLimit(boostTier);

		const serverSounds = useMemo(() => {
			return effectiveGuildId ? SoundboardStore.getGuildSounds(effectiveGuildId) : [];
		}, [effectiveGuildId, SoundboardStore.guildSounds]);

		const personalSounds = SoundboardStore.personalSounds;
		const defaultSounds = DEFAULT_SOUNDBOARD_SOUNDS;

		// Filtering sounds
		const q = searchQuery.toLowerCase().trim();
		const filterFn = useCallback(
			(s: SoundboardSound) => {
				if (!q) return true;
				return s.name.toLowerCase().includes(q) || s.emoji.includes(q);
			},
			[q],
		);

		const filteredServerSounds = useMemo(() => serverSounds.filter(filterFn), [serverSounds, filterFn]);
		const filteredPersonalSounds = useMemo(() => personalSounds.filter(filterFn), [personalSounds, filterFn]);
		const filteredDefaultSounds = useMemo(() => defaultSounds.filter(filterFn), [defaultSounds, filterFn]);

		const totalCount =
			filteredServerSounds.length + filteredPersonalSounds.length + filteredDefaultSounds.length;

		const handleOpenAddForm = (target: 'server' | 'personal') => {
			setAddFormTarget(target);
			setSoundName('');
			setSoundEmoji('🔊');
			setSoundUrl('');
			setUploadedFileName(null);
			setFormError(null);
			setShowAddForm(true);
		};

		const handleAudioFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (!file) return;

			if (file.size > 3 * 1024 * 1024) {
				setFormError('Audio file is too large (maximum 3 MB)');
				return;
			}

			try {
				const dataUrl = await readBlobAsDataUrl(file);
				setSoundUrl(dataUrl);
				setUploadedFileName(file.name);
				setFormError(null);
				if (!soundName) {
					const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
					setSoundName(cleanName.slice(0, 32));
				}
			} catch {
				setFormError('Failed to read selected audio file');
			}
		};

		const handlePreviewAudio = () => {
			if (!soundUrl.trim()) return;
			try {
				const audio = new Audio(soundUrl.trim());
				audio.volume = SoundboardStore.volume / 100;
				void audio.play().catch(() => {
					setFormError('Failed to load audio. Make sure the URL is a direct .mp3, .wav, or .ogg link.');
				});
			} catch {
				setFormError('Invalid audio URL');
			}
		};

		const handleSaveSound = () => {
			const trimmedName = soundName.trim();
			const trimmedUrl = soundUrl.trim();
			const trimmedEmoji = soundEmoji.trim() || '🔊';

			if (!trimmedName) {
				setFormError('Sound name is required');
				return;
			}
			if (!trimmedUrl) {
				setFormError('Audio URL is required');
				return;
			}

			if (addFormTarget === 'server') {
				if (!effectiveGuildId) {
					setFormError('No server selected');
					return;
				}
				if (!canManageServerSounds) {
					setFormError('Only server admins can add server sounds');
					return;
				}
				const result = SoundboardStore.addGuildSound(
					effectiveGuildId,
					trimmedName,
					trimmedEmoji,
					trimmedUrl,
					boostTier,
				);
				if (!result.success) {
					setFormError(result.error || 'Failed to add server sound');
					return;
				}
			} else {
				if (!isNDPlus) {
					setFormError('ND+ membership is required to save personal sounds');
					return;
				}
				const result = SoundboardStore.addPersonalSound(
					trimmedName,
					trimmedEmoji,
					trimmedUrl,
					isNDPlus,
				);
				if (!result.success) {
					setFormError(result.error || 'Failed to add personal sound');
					return;
				}
			}

			setShowAddForm(false);
			setFormError(null);
		};

		const handlePlay = (soundId: string) => {
			void SoundboardStore.playSound(soundId);
		};

		const handlePreview = (e: React.MouseEvent, sound: SoundboardSound) => {
			e.stopPropagation();
			SoundboardStore.previewSound(sound);
		};

		const handleDeleteServerSound = (e: React.MouseEvent, soundId: string) => {
			e.stopPropagation();
			if (!effectiveGuildId) return;
			SoundboardStore.removeGuildSound(effectiveGuildId, soundId);
		};

		const handleDeletePersonalSound = (e: React.MouseEvent, soundId: string) => {
			e.stopPropagation();
			SoundboardStore.removePersonalSound(soundId);
		};

		const isServerFull = serverSounds.length >= maxServerSlots;
		const isPersonalFull = personalSounds.length >= MAX_PERSONAL_SOUNDBOARD_SLOTS;

		return (
			<div
				className={clsx(styles.popoutContainer, isModal && styles.modalMode)}
				data-flx="voice.soundboard.popout"
			>
				{/* Top Unified Header (No double header!) */}
				<div className={styles.header}>
					<div className={styles.titleRow}>
						<span className={styles.title}>
							<WaveformIcon size={20} weight="bold" className={styles.titleIcon} />
							Soundboard
						</span>
						{onClose && (
							<button
								type="button"
								onClick={onClose}
								className={styles.closeButton}
								aria-label="Close Soundboard"
								data-flx="voice.soundboard.close-btn"
							>
								<XIcon size={18} />
							</button>
						)}
					</div>

					{/* Search bar */}
					<div className={styles.searchContainer}>
						<MagnifyingGlassIcon size={16} weight="bold" className={styles.searchIcon} />
						<input
							type="text"
							className={styles.searchInput}
							placeholder="Search sounds..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							autoFocus
							data-flx="voice.soundboard.search-input"
						/>
						{searchQuery && (
							<button
								type="button"
								onClick={() => setSearchQuery('')}
								className={styles.clearSearchBtn}
								aria-label="Clear search"
							>
								<XIcon size={14} />
							</button>
						)}
					</div>

					{/* Filter tabs */}
					<div className={styles.tabsRow}>
						<button
							type="button"
							className={clsx(styles.tabBtn, activeTab === 'all' && styles.tabBtnActive)}
							onClick={() => setActiveTab('all')}
						>
							All <span className={styles.tabBadge}>{totalCount}</span>
						</button>
						{guild && (
							<button
								type="button"
								className={clsx(styles.tabBtn, activeTab === 'server' && styles.tabBtnActive)}
								onClick={() => setActiveTab('server')}
							>
								<ShieldCheckIcon size={14} weight="bold" />
								{guild.name}
								<span className={styles.tabBadge}>
									{serverSounds.length}/{maxServerSlots}
								</span>
							</button>
						)}
						<button
							type="button"
							className={clsx(styles.tabBtn, activeTab === 'personal' && styles.tabBtnActive)}
							onClick={() => setActiveTab('personal')}
						>
							<SparkleIcon size={14} weight="fill" style={{color: '#f47b67'}} />
							ND+ Sounds
							<span className={styles.tabBadge}>
								{isNDPlus ? `${personalSounds.length}/${MAX_PERSONAL_SOUNDBOARD_SLOTS}` : 'Locked'}
							</span>
						</button>
						<button
							type="button"
							className={clsx(styles.tabBtn, activeTab === 'default' && styles.tabBtnActive)}
							onClick={() => setActiveTab('default')}
						>
							Defaults <span className={styles.tabBadge}>{defaultSounds.length}</span>
						</button>
					</div>
				</div>

				{/* Scrollable Sound List */}
				<div className={styles.contentScroller}>
					{/* 1. Server Sounds Section */}
					{(activeTab === 'all' || activeTab === 'server') && guild && (
						<div className={styles.section}>
							<div className={styles.sectionHeader}>
								<div className={styles.sectionTitleGroup}>
									<span className={styles.sectionTitle}>
										{guild.name} Soundboard
									</span>
									<span
										className={clsx(
											styles.slotBadge,
											isServerFull ? styles.slotBadgeFull : styles.slotBadgeHighlight,
										)}
									>
										{serverSounds.length} / {maxServerSlots} Slots
										{boostTier > 0 && ` • Level ${boostTier}`}
									</span>
								</div>

								{canManageServerSounds ? (
									<button
										type="button"
										className={styles.addBtnSmall}
										onClick={() => handleOpenAddForm('server')}
										disabled={isServerFull}
										title={isServerFull ? 'Server slots full! Boost server for more slots' : 'Add sound to server'}
										data-flx="voice.soundboard.add-server-sound"
									>
										<PlusIcon size={14} weight="bold" />
										Add Sound
									</button>
								) : (
									<span className={styles.adminOnlyTag} title="Only server admins can add sounds">
										Admin Locked
									</span>
								)}
							</div>

							{filteredServerSounds.length > 0 ? (
								<div className={styles.soundGrid}>
									{filteredServerSounds.map((sound) => (
										<button
											key={sound.id}
											type="button"
											className={styles.soundCard}
											onClick={() => handlePlay(sound.id)}
											title={`Play ${sound.name} into Voice Channel`}
										>
											<span className={styles.soundEmoji}>{sound.emoji}</span>
											<span className={styles.soundName}>{sound.name}</span>
											<button
												type="button"
												className={styles.previewSoundBtn}
												onClick={(e) => handlePreview(e, sound)}
												title="Preview sound locally"
												aria-label="Preview sound"
											>
												<PlayIcon size={12} weight="fill" />
											</button>
											{canManageServerSounds && (
												<button
													type="button"
													className={styles.deleteSoundBtn}
													onClick={(e) => handleDeleteServerSound(e, sound.id)}
													title="Delete sound from server"
													aria-label="Delete sound"
												>
													<TrashIcon size={14} />
												</button>
											)}
										</button>
									))}
								</div>
							) : (
								<div className={styles.emptyState}>
									<span>No server sounds {searchQuery ? 'matching search' : 'added yet'}</span>
									{canManageServerSounds && !searchQuery && (
										<span>Admins can upload up to {maxServerSlots} sounds for this server!</span>
									)}
								</div>
							)}
						</div>
					)}

					{/* 2. ND+ Personal Sounds Section */}
					{(activeTab === 'all' || activeTab === 'personal') && (
						<div className={styles.section}>
							<div className={styles.sectionHeader}>
								<div className={styles.sectionTitleGroup}>
									<span className={styles.sectionTitle}>
										<SparkleIcon size={14} weight="fill" style={{color: '#f47b67'}} />
										ND+ Personal Sounds
									</span>
									<span
										className={clsx(
											styles.slotBadge,
											isPersonalFull ? styles.slotBadgeFull : styles.slotBadgeHighlight,
										)}
									>
										{isNDPlus
											? `${personalSounds.length} / ${MAX_PERSONAL_SOUNDBOARD_SLOTS} Slots`
											: 'ND+ Locked'}
									</span>
								</div>

								{isNDPlus ? (
									<button
										type="button"
										className={styles.addBtnSmall}
										onClick={() => handleOpenAddForm('personal')}
										disabled={isPersonalFull}
										title={isPersonalFull ? 'Personal slots full (10/10)' : 'Add personal sound'}
										data-flx="voice.soundboard.add-personal-sound"
									>
										<PlusIcon size={14} weight="bold" />
										Add Sound
									</button>
								) : (
									<button
										type="button"
										className={styles.ndPlusBadgeBtn}
										onClick={() => PremiumModalCommands.open()}
										title="Unlock 10 personal sounds with ND+"
									>
										<SparkleIcon size={12} weight="fill" />
										Unlock ND+
									</button>
								)}
							</div>

							{isNDPlus ? (
								filteredPersonalSounds.length > 0 ? (
									<div className={styles.soundGrid}>
										{filteredPersonalSounds.map((sound) => (
											<button
												key={sound.id}
												type="button"
												className={styles.soundCard}
												onClick={() => handlePlay(sound.id)}
												title={`Play ${sound.name} into Voice Channel`}
											>
												<span className={styles.soundEmoji}>{sound.emoji}</span>
												<span className={styles.soundName}>{sound.name}</span>
												<button
													type="button"
													className={styles.previewSoundBtn}
													onClick={(e) => handlePreview(e, sound)}
													title="Preview sound locally"
													aria-label="Preview sound"
												>
													<PlayIcon size={12} weight="fill" />
												</button>
												<button
													type="button"
													className={styles.deleteSoundBtn}
													onClick={(e) => handleDeletePersonalSound(e, sound.id)}
													title="Delete personal sound"
													aria-label="Delete personal sound"
												>
													<TrashIcon size={14} />
												</button>
											</button>
										))}
									</div>
								) : (
									<div className={styles.emptyState}>
										<span>No personal sounds {searchQuery ? 'matching search' : 'added yet'}</span>
										{!searchQuery && (
											<span>You can save up to {MAX_PERSONAL_SOUNDBOARD_SLOTS} personal sounds to play anywhere!</span>
										)}
									</div>
								)
							) : (
								<div className={styles.upsellCard}>
									<div className={styles.upsellLeft}>
										<SparkleIcon size={28} weight="fill" className={styles.upsellSparkle} />
										<div className={styles.upsellText}>
											<span className={styles.upsellTitle}>
												Unlock 10 Personal Soundboard Slots
											</span>
											<span className={styles.upsellDesc}>
												ND+ members can upload up to 10 custom sounds and play them in any server or voice call!
											</span>
										</div>
									</div>
									<button
										type="button"
										className={styles.upsellBtn}
										onClick={() => PremiumModalCommands.open()}
									>
										Get ND+
									</button>
								</div>
							)}
						</div>
					)}

					{/* 3. Default Presets Section */}
					{(activeTab === 'all' || activeTab === 'default') && (
						<div className={styles.section}>
							<div className={styles.sectionHeader}>
								<div className={styles.sectionTitleGroup}>
									<span className={styles.sectionTitle}>Built-in Sounds</span>
									<span className={styles.slotBadge}>{defaultSounds.length} Presets</span>
								</div>
							</div>

							<div className={styles.soundGrid}>
								{filteredDefaultSounds.map((sound) => (
									<button
										key={sound.id}
										type="button"
										className={styles.soundCard}
										onClick={() => handlePlay(sound.id)}
										title={`Play ${sound.name} into Voice Channel`}
									>
										<span className={styles.soundEmoji}>{sound.emoji}</span>
										<span className={styles.soundName}>{sound.name}</span>
										<button
											type="button"
											className={styles.previewSoundBtn}
											onClick={(e) => handlePreview(e, sound)}
											title="Preview sound locally"
											aria-label="Preview sound"
										>
											<PlayIcon size={12} weight="fill" />
										</button>
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Add Custom Sound Inline Form */}
				{showAddForm && (
					<div className={styles.customSoundForm} data-flx="voice.soundboard.add-form">
						<div className={styles.formHeader}>
							<span className={styles.formTitle}>
								Add {addFormTarget === 'server' ? `${guild?.name ?? 'Server'} Sound` : 'Personal Sound'}
							</span>
							<button
								type="button"
								onClick={() => setShowAddForm(false)}
								className={styles.btnSecondary}
							>
								<XIcon size={16} />
							</button>
						</div>

						{/* Destination selection if user has permissions for both */}
						{canManageServerSounds && isNDPlus && (
							<div className={styles.targetSelector}>
								<label className={styles.targetOption}>
									<input
										type="radio"
										name="soundTarget"
										checked={addFormTarget === 'server'}
										onChange={() => setAddFormTarget('server')}
									/>
									Server ({guild?.name})
								</label>
								<label className={styles.targetOption}>
									<input
										type="radio"
										name="soundTarget"
										checked={addFormTarget === 'personal'}
										onChange={() => setAddFormTarget('personal')}
									/>
									Personal (ND+)
								</label>
							</div>
						)}

						{/* Local Audio File Upload */}
						<div className={styles.fileUploadRow}>
							<input
								type="file"
								ref={fileInputRef}
								accept="audio/*,.mp3,.wav,.ogg"
								style={{display: 'none'}}
								onChange={handleAudioFileUpload}
							/>
							<button
								type="button"
								className={styles.uploadFileBtn}
								onClick={() => fileInputRef.current?.click()}
							>
								<UploadSimpleIcon size={16} weight="bold" />
								{uploadedFileName ? `File: ${uploadedFileName}` : 'Choose Audio File (.mp3, .wav)'}
							</button>
						</div>

						<div className={styles.orDivider}>
							<span>or paste direct link</span>
						</div>

						<input
							type="text"
							className={styles.formInput}
							placeholder="Sound Name (e.g. GG, Laugh, Tada)"
							value={soundName}
							onChange={(e) => {
								setSoundName(e.target.value);
								setFormError(null);
							}}
							maxLength={32}
						/>

						<div style={{display: 'flex', gap: '8px'}}>
							<input
								type="text"
								className={styles.formInput}
								style={{width: '64px', textAlign: 'center', fontSize: '1.2rem'}}
								placeholder="🔊"
								value={soundEmoji}
								onChange={(e) => setSoundEmoji(e.target.value)}
								maxLength={4}
							/>
							<input
								type="text"
								className={styles.formInput}
								style={{flex: 1}}
								placeholder="Audio URL (.mp3, .wav, or .ogg link)"
								value={soundUrl}
								onChange={(e) => {
									setSoundUrl(e.target.value);
									setUploadedFileName(null);
									setFormError(null);
								}}
							/>
						</div>

						{formError && <span className={styles.formError}>{formError}</span>}

						<div className={styles.formActions}>
							<button
								type="button"
								className={styles.btnPreview}
								onClick={handlePreviewAudio}
								disabled={!soundUrl.trim()}
								title="Test play audio before saving"
							>
								<PlayIcon size={14} weight="fill" />
								Test Audio
							</button>
							<button
								type="button"
								className={styles.btnSecondary}
								onClick={() => setShowAddForm(false)}
							>
								Cancel
							</button>
							<button
								type="button"
								className={styles.btnPrimary}
								onClick={handleSaveSound}
								disabled={!soundName.trim() || !soundUrl.trim()}
							>
								Save Sound
							</button>
						</div>
					</div>
				)}

				{/* Bottom Volume Control */}
				<div className={styles.footer}>
					<div className={styles.volumeControl}>
						{SoundboardStore.volume === 0 ? (
							<SpeakerSlashIcon size={18} />
						) : SoundboardStore.volume <= 50 ? (
							<SpeakerLowIcon size={18} />
						) : (
							<SpeakerHighIcon size={18} />
						)}
						<input
							type="range"
							className={styles.volumeSlider}
							min="0"
							max="100"
							value={SoundboardStore.volume}
							onChange={(e) => SoundboardStore.setVolume(Number(e.target.value))}
							aria-label="Soundboard Volume"
						/>
						<span className={styles.volumeLabel}>{SoundboardStore.volume}%</span>
					</div>
				</div>
			</div>
		);
	},
);
